import React from 'react';
import { Card, Typography, Box } from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import IconPopoverMenu from '../IconPopoverMenu/IconPopoverMenu';
import EditIcon from '../CustomSVGIcons/EditIcon';
import { TSkill } from '../../types/global/helper';
import { renderSkillLevel } from '../WorkProfile/SkillCard';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

export interface IWorkExperieceCardProps {
    experience: TSkill;
    onDelete?: () => void;
    onDuplicate?: () => void;
    onEdit?: () => void;
}

const WorkExperienceCard: React.FC<IWorkExperieceCardProps> = ({ experience, onDelete, onDuplicate, onEdit }) => {
    const { themeStyle } = useThemeContext();
    const handleMenuAction = (action: 'edit' | 'delete' | 'duplicate') => {
        switch (action) {
            case 'delete':
                onDelete && onDelete();
                break;
            case 'duplicate':
                onDuplicate && onDuplicate();
                break;
            case 'edit':
                onEdit && onEdit();
                break;
        }
    };
    const { description = '', expertise = 'beginner', skill = '', years_of_experience = 1 } = experience || {};
    return (
        <Card
            sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                maxWidth: '100%',
                position: 'relative',
                my: 1,
                backgroundColor: theme[themeStyle].secondaryBg,
                border: `1px solid ${theme[themeStyle].tag_white}`,
                borderRadius: 2,
                boxShadow: `0px 2px 4px ${theme[themeStyle].shadowColor}`,
                '&:hover': {
                    boxShadow: `0px 4px 8px ${theme[themeStyle].shadowColor}`,
                    border: `1px solid ${theme[themeStyle].highlight}`,
                },
                transition: 'box-shadow 0.2s, border 0.2s',
            }}>
            {/* Left Icon - Suitcase */}
            <WorkIcon
                sx={{
                    fontSize: 40,
                    mr: 2,
                    color: theme[themeStyle].highlight,
                }}
            />

            {/* Text Content */}
            <Box sx={{ flexGrow: 1 }}>
                {skill && (
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: 'inherit',
                            fontWeight: 600,
                        }}>
                        {skill} - {years_of_experience} Years
                    </Typography>
                )}
                {description && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: 'inherit',
                        }}>
                        {description}
                    </Typography>
                )}

                {/* Expertise Levels */}
                {expertise && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        {renderSkillLevel(expertise, themeStyle)}
                    </Box>
                )}
            </Box>

            {/* Pencil Edit Menu (Top Right) */}
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <IconPopoverMenu
                    icon={<EditIcon stroke={theme[themeStyle].tag_white} height={'24px'} width={'24px'} />}
                    onOptionClick={handleMenuAction}
                    iconButtonStyle={{
                        color: theme[themeStyle].tag_white,
                    }}
                />
            </Box>
        </Card>
    );
};

export { WorkExperienceCard };
